import { useState } from 'react';
import { FileText, RefreshCw } from 'lucide-react';
import { useAllRuntimeLogs } from '../../api/hooks';
import { SectionHeader, EmptyState, Spinner } from '../../components/ui';
import { fmtDateTime, cn } from '../../lib/utils';

const LEVELS = ['ALL', 'ERROR', 'WARN', 'INFO', 'DEBUG'];

const LEVEL_STYLE = {
  ERROR: 'text-bear',
  WARN:  'text-warn',
  INFO:  'text-txt-secondary',
  DEBUG: 'text-txt-muted',
};

function getTodayIstDate() {
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return fmt.format(new Date());
}

function LogLine({ log, showDeployment }) {
  const color = LEVEL_STYLE[log.level?.toUpperCase()] || 'text-txt-muted';
  return (
    <div className="flex items-start gap-3 py-1 px-3 hover:bg-bg-hover/50 rounded font-mono text-xs border-b border-bg-border/20 last:border-0">
      <span className="text-txt-muted shrink-0 w-32 tabular-nums">{fmtDateTime(log.created_at)}</span>
      <span className={cn('shrink-0 w-12 uppercase text-2xs font-bold', color)}>{log.level}</span>
      {showDeployment && (
        <span className="text-txt-muted shrink-0 w-16 tabular-nums text-2xs">#{log.deployment_id ?? '?'}</span>
      )}
      <span className="text-txt-secondary break-all leading-relaxed">{log.message}</span>
    </div>
  );
}

export default function RuntimeLogsPage() {
  const [levelFilter, setLevel]     = useState('ALL');
  const todayIst = getTodayIstDate();
  const [fromDate, setFromDate] = useState(todayIst);
  const [toDate, setToDate] = useState(todayIst);
  const [appliedRange, setAppliedRange] = useState({ from: todayIst, to: todayIst });

  const dateQueryParams = {
    from_date: appliedRange.from || todayIst,
    to_date: appliedRange.to || todayIst,
  };

  const { data: rawLogs = [], isLoading, refetch, error } = useAllRuntimeLogs(dateQueryParams);

  // Newest first, then apply level filter
  const allSorted = [...rawLogs].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  const filtered  = levelFilter === 'ALL'
    ? allSorted
    : allSorted.filter((l) => l.level?.toUpperCase() === levelFilter);

  const handleApplyDates = () => {
    const start = fromDate || todayIst;
    const end = toDate || todayIst;
    if (start > end) return;
    setAppliedRange({ from: start, to: end });
  };
  const handleResetDates = () => {
    setFromDate(todayIst);
    setToDate(todayIst);
    setAppliedRange({ from: todayIst, to: todayIst });
  };

  const terminalLabel = `all-deployments.log — ${filtered.length} lines`;

  return (
    <div className="max-w-6xl space-y-5">
      <SectionHeader
        title="Runtime Logs"
        subtitle="Strategy worker execution logs"
        actions={
          <button onClick={refetch} className="btn-ghost flex items-center gap-1.5 text-xs">
            <RefreshCw size={12} /> Refresh
          </button>
        }
      />

      {/* Controls */}
      <div className="panel p-4 flex items-center gap-3 flex-wrap">
        <FileText size={13} className="text-amber shrink-0" />
        <span className="text-xs text-txt-muted font-mono">From:</span>
        <input
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          className="input-base w-40 text-xs"
        />
        <span className="text-xs text-txt-muted font-mono">To:</span>
        <input
          type="date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          className="input-base w-40 text-xs"
        />
        <button
          onClick={handleApplyDates}
          disabled={!fromDate || !toDate || fromDate > toDate}
          className="btn-primary text-xs py-1.5"
        >
          Apply
        </button>
        <button onClick={handleResetDates} className="btn-ghost text-xs py-1.5">
          Reset
        </button>

        {/* Level filter — always visible */}
        <div className="flex items-center gap-2 ml-2">
          {LEVELS.map((l) => (
            <button
              key={l}
              onClick={() => setLevel(l)}
              className={cn(
                'px-2 py-1 rounded text-2xs font-mono transition-colors border',
                levelFilter === l
                  ? 'bg-amber/10 border-amber/30 text-amber'
                  : 'border-transparent text-txt-muted hover:text-txt-secondary',
              )}
              >
                {l}
              </button>
            ))}
        </div>

        <span className="text-2xs font-mono text-txt-muted ml-auto">
          {filtered.length} / {allSorted.length} lines
        </span>
      </div>

      {/* Status label */}
      <p className="text-2xs font-mono text-txt-muted px-1">
        Showing latest records across all deployments
        {` • Date: ${appliedRange.from} to ${appliedRange.to} (IST)`}
      </p>

      {/* Log terminal */}
      <div className="panel overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center py-12"><Spinner size={18} /></div>
        ) : error ? (
          <div className="p-8">
            <EmptyState title="Could not load logs" description={error.message} />
          </div>
        ) : (
          <div className="bg-bg-base rounded-b-lg max-h-[540px] overflow-y-auto p-2 no-scrollbar">
            {/* Terminal header */}
            <div className="flex items-center gap-2 px-3 py-2 border-b border-bg-border mb-1">
              <span className="w-2.5 h-2.5 rounded-full bg-bear/60" />
              <span className="w-2.5 h-2.5 rounded-full bg-warn/60" />
              <span className="w-2.5 h-2.5 rounded-full bg-bull/60" />
              <span className="text-2xs font-mono text-txt-muted ml-3">{terminalLabel}</span>
            </div>

            {filtered.length === 0 ? (
              <div className="text-txt-muted text-xs font-mono text-center py-8">
                <p>
                  {rawLogs.length === 0
                    ? 'No runtime logs yet — execution details will appear here once a strategy runs.'
                    : 'No log lines match the current filter — try clearing it.'}
                </p>
                {rawLogs.length > 0 && (
                  <button onClick={() => setLevel('ALL')} className="btn-ghost text-2xs mt-2">
                    Clear filter
                  </button>
                )}
              </div>
            ) : (
              <div>
                {filtered.map((l, i) => (
                  <LogLine key={l.id ?? i} log={l} showDeployment />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
